<template>
    <admin-page :result="result" description="Crea encuestas, edita sus opciones y mira los votos">
        <div class="survey-toolbar">
            <segmented-tabs class="survey-tabs" :options="filterOptions" :value="filter" @change="onChangeFilter" />
            <v-btn color="primary" class="black--text survey-new-btn" depressed @click="startCreate">
                <v-icon left size="18">mdi-plus</v-icon>
                Nueva encuesta
            </v-btn>
        </div>

        <skeleton-loader v-if="loading" type="rows" :count="4" class="mt-4" />

        <!-- FORM DE CREACION -->
        <div v-else-if="creating" class="survey-panel">
            <div class="survey-panel-title">Nueva encuesta</div>

            <kimsa-text-field :value="draft.name" @change="draft.name = $event" label="Nombre"
                placeholder="¿Qué te gustaría ver en la app?" dense hide-details />

            <div class="survey-row mt-4">
                <div>
                    <div class="survey-option-name">Activar al crear</div>
                    <div class="survey-hint">Si la activas, se desactivan las demás.</div>
                </div>
                <kimsa-toggle :value="draft.active" @change="draft.active = $event" />
            </div>

            <div class="survey-section-title mt-4">Opciones</div>
            <div v-if="draft.options.length" class="survey-draft-options">
                <div v-for="(option, index) in draft.options" :key="index" class="survey-draft-option">
                    <span>{{ option }}</span>
                    <button-tooltip tooltip="Quitar">
                        <template #activator="{ on, attrs }">
                            <button class="survey-icon-btn" v-on="on" v-bind="attrs"
                                @click="draft.options.splice(index, 1)">
                                <v-icon size="13">mdi-close</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                </div>
            </div>
            <div v-else class="survey-hint">Todavía no agregaste opciones.</div>

            <div class="survey-add">
                <kimsa-text-field class="survey-add-field" :value="newOption" @change="newOption = $event"
                    placeholder="Nueva opción" dense hide-details />
                <v-btn color="secondary" class="survey-add-btn" depressed :disabled="!newOption"
                    @click="addDraftOption">
                    Agregar
                </v-btn>
            </div>

            <div class="survey-actions">
                <v-btn text class="survey-cancel" @click="creating = false">Cancelar</v-btn>
                <v-btn color="primary" class="black--text survey-add-btn" depressed :loading="saving"
                    :disabled="!draft.name || !draft.options.length" @click="$refs.confirmCreate.show()">
                    Crear encuesta
                </v-btn>
            </div>
        </div>

        <!-- DETALLE DE UNA ENCUESTA -->
        <div v-else-if="survey" class="survey-panel">
            <div class="survey-head">
                <div>
                    <div class="survey-name">{{ survey.name }}</div>
                    <div class="survey-total">{{ totalVotes }} {{ totalVotes === 1 ? 'voto' : 'votos' }} en total</div>
                </div>
                <div class="survey-head-actions">
                    <kimsa-toggle :value="survey.active === true" @change="onToggleActive" />
                    <span class="survey-active-label">{{ survey.active ? 'Activa' : 'Inactiva' }}</span>
                    <button-tooltip tooltip="Volver a la lista">
                        <template #activator="{ on, attrs }">
                            <button class="survey-icon-btn survey-icon-btn--plain" v-on="on" v-bind="attrs"
                                @click="survey = null">
                                <v-icon size="16">mdi-close</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                </div>
            </div>

            <div v-if="options.length" class="survey-options">
                <div v-for="option in sortedOptions" :key="option.id" class="survey-option">
                    <div class="survey-option-head">
                        <span class="survey-option-name">{{ option.name }}</span>
                        <span class="survey-option-votes">
                            {{ option.votes || 0 }} · {{ percentage(option) }}%
                            <button-tooltip tooltip="Borrar opción">
                                <template #activator="{ on, attrs }">
                                    <button class="survey-icon-btn" v-on="on" v-bind="attrs"
                                        @click="showDeleteOption(option)">
                                        <v-icon size="14">mdi-trash-can-outline</v-icon>
                                    </button>
                                </template>
                            </button-tooltip>
                        </span>
                    </div>
                    <div class="survey-bar">
                        <div class="survey-bar-fill" :style="{ width: `${percentage(option)}%` }" />
                    </div>
                </div>
            </div>
            <div v-else class="survey-hint mt-3">Esta encuesta todavía no tiene opciones.</div>

            <div class="survey-add">
                <kimsa-text-field class="survey-add-field" :value="newOption" @change="newOption = $event"
                    placeholder="Nueva opción" dense hide-details />
                <v-btn color="secondary" class="survey-add-btn" depressed :loading="saving" :disabled="!newOption"
                    @click="addOption">
                    Agregar
                </v-btn>
            </div>
        </div>

        <!-- LISTA -->
        <template v-else>
            <div v-if="filteredSurveys.length" class="survey-list">
                <div v-for="item in filteredSurveys" :key="item.id" class="survey-list-row" @click="openSurvey(item)">
                    <div class="survey-list-body">
                        <div class="survey-list-name">{{ item.name }}</div>
                        <div class="survey-list-meta">{{ item.dateLabel }}</div>
                    </div>
                    <span class="survey-chip" :class="item.active ? 'survey-chip--on' : null">
                        {{ item.active ? 'Activa' : 'Inactiva' }}
                    </span>
                    <v-icon size="18" color="txtMuted">mdi-chevron-right</v-icon>
                </div>
            </div>

            <div v-else class="survey-empty">
                <v-icon size="34" color="txtMuted">mdi-poll</v-icon>
                <span>No hay encuestas.</span>
            </div>
        </template>

        <kimsa-confirm ref="confirmCreate" show-cancel title="Crear encuesta" :content="createConfirmContent"
            :persistent="false" :close-on-confirm="false" :loading="saving" @confirm="createSurvey" />
        <kimsa-confirm ref="confirmOption" alert-mode show-cancel title="Borrar opción" :content="optionConfirmContent"
            :persistent="false" :close-on-confirm="false" :loading="saving" @confirm="deleteOption"
            @cancel="pendingOption = null" />
        <kimsa-confirm ref="confirmActive" show-cancel title="Cambiar estado" :content="activeConfirmContent"
            :persistent="false" :close-on-confirm="false" :loading="saving" @confirm="saveActive"
            @cancel="pendingActive = null" />
    </admin-page>
</template>

<script>
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaToggle from '@/components/common/inputs/KimsaToggle.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import SegmentedTabs from '@/components/common/SegmentedTabs.vue'
import ButtonTooltip from '@/components/common/ButtonTooltip.vue'
import SkeletonLoader from '@/components/common/SkeletonLoader.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import { db, Timestamp } from '@/utils/Firebase'
import { adminRouteGuard } from '@/utils/Admin'

export default {
    name: 'AdminSurveys',
    components: { KimsaTextField, KimsaToggle, KimsaConfirm, SegmentedTabs, ButtonTooltip, SkeletonLoader, AdminPage },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            surveys: [],
            survey: null,
            options: [],
            filter: 'all',
            filterOptions: [
                { name: 'Todas', value: 'all' },
                { name: 'Activas', value: 'active' },
            ],
            creating: false,
            draft: { name: '', active: false, options: [] },
            newOption: '',
            loading: false,
            saving: false,
            pendingOption: null,
            pendingActive: null,
            result: null,
        }
    },
    computed: {
        filteredSurveys(vm = this) {
            if (vm.filter === 'active') return vm.surveys.filter(survey => survey.active)
            return vm.surveys
        },
        sortedOptions(vm = this) {
            return [...vm.options].sort((a, b) => (b.votes || 0) - (a.votes || 0))
        },
        totalVotes(vm = this) {
            return vm.options.reduce((total, option) => total + (option.votes || 0), 0)
        },
        createConfirmContent(vm = this) {
            return `Vas a crear <b>${vm.draft.name}</b> con ${vm.draft.options.length} opción(es), ${vm.draft.active ? 'activa' : 'inactiva'}.`
        },
        optionConfirmContent(vm = this) {
            return `Vas a borrar la opción <b>${vm.pendingOption?.name || ''}</b> y sus ${vm.pendingOption?.votes || 0} voto(s).`
        },
        activeConfirmContent(vm = this) {
            if (vm.pendingActive) return `Vas a activar <b>${vm.survey?.name || ''}</b>. Se desactivan las demás encuestas.`
            return `Vas a desactivar <b>${vm.survey?.name || ''}</b>. Deja de mostrarse en la app.`
        },
    },
    mounted() {
        this.loadSurveys()
    },
    methods: {
        percentage(option) {
            const vm = this
            if (!vm.totalVotes) return 0
            return Math.round(((option.votes || 0) * 100) / vm.totalVotes)
        },
        onChangeFilter(filter) {
            this.filter = filter
        },
        startCreate() {
            const vm = this
            vm.creating = true
            vm.survey = null
            vm.result = null
            vm.draft = { name: '', active: false, options: [] }
            vm.newOption = ''
        },
        addDraftOption() {
            const vm = this
            vm.draft.options.push(vm.newOption.trim())
            vm.newOption = ''
        },
        async loadSurveys() {
            const vm = this
            await vm.withLoading(async () => {
                const snapshot = await db.collection('surveys').get()
                vm.surveys = snapshot.docs.map(doc => {
                    const data = doc.data() || {}
                    const date = data.createdAt || null
                    const parsed = date ? vm.$moment(date.toDate ? date.toDate() : date) : null
                    return {
                        id: doc.id,
                        ...data,
                        dateLabel: parsed && parsed.isValid() ? parsed.format('DD/MM/YYYY') : 'sin fecha',
                        timestamp: parsed && parsed.isValid() ? parsed.valueOf() : 0,
                    }
                }).sort((a, b) => b.timestamp - a.timestamp)
            })
        },
        async openSurvey(survey) {
            const vm = this
            vm.result = null
            vm.creating = false
            await vm.withLoading(async () => {
                vm.survey = survey
                const optionsRef = await db.collection('surveys').doc(survey.id).collection('options').get()
                vm.options = optionsRef.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            })
        },
        async deactivateOthers(exceptId) {
            const snapshot = await db.collection('surveys').where('active', '==', true).get()
            await Promise.all(snapshot.docs
                .filter(doc => doc.id !== exceptId)
                .map(doc => doc.ref.update({ active: false })))
        },
        async createSurvey() {
            const vm = this
            vm.saving = true
            vm.result = null
            try {
                const ref = await db.collection('surveys').add({
                    name: vm.draft.name.trim(),
                    active: vm.draft.active,
                    createdAt: Timestamp.now(),
                })

                await Promise.all(vm.draft.options.map(name =>
                    db.collection('surveys').doc(ref.id).collection('options').add({ name, votes: 0 })
                ))

                if (vm.draft.active) await vm.deactivateOthers(ref.id)

                vm.result = { type: 'success', message: `Encuesta "${vm.draft.name.trim()}" creada.` }
                vm.creating = false
                await vm.loadSurveys()
            } catch (err) {
                console.error('[adminSurveys] create', err)
                vm.result = { type: 'error', message: err.message || 'Error al crear la encuesta.' }
            } finally {
                vm.saving = false
                vm.$refs.confirmCreate?.hide()
            }
        },
        async addOption() {
            const vm = this
            vm.saving = true
            vm.result = null
            try {
                const ref = await db.collection('surveys').doc(vm.survey.id).collection('options').add({
                    name: vm.newOption.trim(),
                    votes: 0,
                })
                vm.options.push({ id: ref.id, name: vm.newOption.trim(), votes: 0 })
                vm.newOption = ''
            } catch (err) {
                console.error('[adminSurveys] addOption', err)
                vm.result = { type: 'error', message: err.message || 'Error al agregar la opción.' }
            } finally {
                vm.saving = false
            }
        },
        showDeleteOption(option) {
            this.pendingOption = option
            this.$refs.confirmOption.show()
        },
        async deleteOption() {
            const vm = this
            vm.saving = true
            try {
                await db.collection('surveys').doc(vm.survey.id).collection('options').doc(vm.pendingOption.id).delete()
                vm.options = vm.options.filter(option => option.id !== vm.pendingOption.id)
            } catch (err) {
                console.error('[adminSurveys] deleteOption', err)
                vm.result = { type: 'error', message: err.message || 'Error al borrar la opción.' }
            } finally {
                vm.saving = false
                vm.pendingOption = null
                vm.$refs.confirmOption?.hide()
            }
        },
        onToggleActive(value) {
            this.pendingActive = value
            this.$refs.confirmActive.show()
        },
        async saveActive() {
            const vm = this
            vm.saving = true
            try {
                await db.collection('surveys').doc(vm.survey.id).update({ active: vm.pendingActive })
                if (vm.pendingActive) await vm.deactivateOthers(vm.survey.id)

                vm.survey = { ...vm.survey, active: vm.pendingActive }
                vm.result = {
                    type: 'success',
                    message: `Encuesta ${vm.pendingActive ? 'activada' : 'desactivada'}.`
                }
                await vm.loadSurveys()
            } catch (err) {
                console.error('[adminSurveys] active', err)
                vm.result = { type: 'error', message: err.message || 'Error al cambiar el estado.' }
            } finally {
                vm.saving = false
                vm.pendingActive = null
                vm.$refs.confirmActive?.hide()
            }
        },
    },
}
</script>

<style scoped>
.survey-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.survey-tabs {
    width: 220px;
}

.survey-new-btn,
.survey-add-btn {
    height: 38px !important;
    border-radius: 8px;
    text-transform: none;
    letter-spacing: normal;
}

.survey-panel {
    margin-top: 18px;
    padding: 16px;
    border-radius: 12px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.survey-panel-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    margin-bottom: 14px;
}

.survey-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.survey-name {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.survey-total {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.survey-head-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.survey-active-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.survey-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.survey-section-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--v-txtMuted-base);
    margin-bottom: 8px;
}

.survey-hint {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.survey-draft-options {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.survey-draft-option {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px 4px 12px;
    border-radius: 20px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.survey-icon-btn {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--v-txtMuted-base);
    transition: background 0.12s, color 0.12s;
}

.survey-icon-btn .v-icon {
    color: inherit;
}

.survey-icon-btn:hover {
    background: rgba(232, 92, 92, 0.12);
    color: var(--v-error-base);
}

.survey-icon-btn--plain:hover {
    background: var(--v-surface-base);
    color: var(--v-txtPrimary-base);
}

.survey-options {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 20px;
}

.survey-option-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 6px;
}

.survey-option-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.survey-option-votes {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
}

.survey-bar {
    height: 10px;
    border-radius: 5px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    overflow: hidden;
}

.survey-bar-fill {
    height: 100%;
    border-radius: 5px;
    background: var(--brand-gradient);
    transition: width 0.3s;
}

.survey-add {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    margin-top: 18px;
    flex-wrap: wrap;
}

.survey-add-field {
    flex: 1;
    min-width: 200px;
}

.survey-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 18px;
}

.survey-cancel {
    text-transform: none;
    letter-spacing: normal;
    color: var(--v-txtSecondary-base);
}

.survey-list {
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.survey-list-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    cursor: pointer;
}

.survey-list-row:hover {
    border-color: var(--v-accent-base);
}

.survey-list-body {
    flex: 1;
    min-width: 0;
}

.survey-list-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.survey-list-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.survey-chip {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
    background: rgba(136, 146, 164, 0.14);
    color: var(--v-txtMuted-base);
}

.survey-chip--on {
    background: rgba(0, 153, 102, 0.14);
    color: var(--v-success-base);
}

.survey-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 32px 12px;
    color: var(--v-txtMuted-base);
    font-size: 14px;
}

@media (max-width: 600px) {
    .survey-tabs {
        width: 100%;
    }

    .survey-new-btn {
        width: 100%;
    }
}
</style>
